import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {Button, Grid, Typography} from "@mui/material";
import React from "react";
import {LabeledValue} from "../../../components/Layout/Page";
import {ArrowForward} from "@mui/icons-material";
import styled from "styled-components";
import {DkvCustomerNameResolveRequest, DkvUpdateCustomerNameRequest} from "../../../app/api/types";
import {useDkvCustomerNameMismatchUpdateMutation, useDkvCustomerNameUpdateMutation} from "../../../app/api/api";

const RightSection = styled.div`
    display: flex;
    justify-content: flex-end;
    min-width: 312px;

    @media (max-width: 767px) {
        min-width: 252px;
    }
`;

interface OrderCustomerNameDialogProps {
    open: boolean;
    onClose: () => void;
    orderId?: number;
    customerName?: string;
    dkvCustomerNumber?: number;
    dkvCustomerName?: string;
}

export default function OrderCustomerNameDialog({
    open,
    onClose,
    orderId,
    customerName,
    dkvCustomerNumber,
    dkvCustomerName,
}: OrderCustomerNameDialogProps) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [updateCustomerName] = useDkvCustomerNameUpdateMutation();
    const [updateNameMismatch] = useDkvCustomerNameMismatchUpdateMutation();

    const handleClose = () => {
        onClose?.();
    };

    const handleNameUpdate = async () => {
        const nameRequest = {
            name: customerName,
            dkvCustomerNumber: dkvCustomerNumber,
        } as DkvUpdateCustomerNameRequest;

        const mismatchRequest = {
            id: orderId,
        } as DkvCustomerNameResolveRequest;

        await updateCustomerName(nameRequest).unwrap();
        await updateNameMismatch(mismatchRequest).unwrap();

        enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
        handleClose();
    };

    const handleMismatchUpdate = async () => {
        const mismatchRequest = {
            id: orderId,
        } as DkvCustomerNameResolveRequest;

        await updateNameMismatch(mismatchRequest).unwrap();

        enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
        handleClose();
    };

    return (
        <DefaultDialog
            open={open}
            onClose={handleClose}
            size={DefaultDialogSize.LARGE}
            titleKey={t("order_changeCustomerName")}
            actions={
                <RightSection>
                    <Button onClick={handleMismatchUpdate}>{t("action_noUpdate")}</Button>
                    <Button onClick={handleNameUpdate}>{t("action_update")}</Button>
                </RightSection>
            }
        >
            <Grid item xs={12}>
                <Typography>{t("order_changeCustomerNameText")}</Typography>
            </Grid>
            <Grid container spacing={3} columns={{sm: 6, md: 12}} sx={{mt: 2}} alignItems="center">
                <Grid item xs={9} md={5}>
                    <LabeledValue label={t("generic_customerName")} content={customerName} md={12} />
                </Grid>
                <Grid item xs={2}>
                    <ArrowForward />
                </Grid>
                <Grid item xs={9} md={5}>
                    <LabeledValue label={t("generic_dkvCustomerName")} content={dkvCustomerName} md={12} />
                </Grid>
            </Grid>
        </DefaultDialog>
    );
}
